import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE_ORIGIN = "https://www.oya-nishikigoi.com";

interface PageSEOProps {
  title: string;
  description: string;
  /** Optional explicit canonical path (defaults to current location pathname). */
  path?: string;
  /** Optional og:image override (absolute URL). */
  image?: string;
  /** og:type, defaults to "website". */
  type?: "website" | "article" | "product";
  /** Whether to mark this page noindex. */
  noindex?: boolean;
  /** Optional JSON-LD structured data object (or array of objects). */
  jsonLd?: object | object[];
}

/**
 * Per-route SEO head tags. Sets a unique <title>, <meta name="description">,
 * self-referencing canonical, and Open Graph / Twitter overrides.
 */
export function PageSEO({
  title,
  description,
  path,
  image,
  type = "website",
  noindex,
  jsonLd,
}: PageSEOProps) {
  const location = useLocation();
  const resolvedPath = path ?? location.pathname;
  const canonical = `${SITE_ORIGIN}${resolvedPath}`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:url" content={canonical} />
      {image ? <meta property="og:image" content={image} /> : null}
      {image ? <meta name="twitter:image" content={image} /> : null}
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : null}
      {jsonLd ? (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      ) : null}
    </Helmet>
  );
}
